// Family completion tracking utilities
import { getVersesForChapter } from './bibleData';

/**
 * Update family completion status when recordings are uploaded
 * @param {string} familyId - The family ID
 * @param {string} book - The book name (e.g., 'genesis')
 * @param {number} chapter - The chapter number
 * @param {Array} verseNumbers - Array of verse numbers that were recorded
 * @param {Object} db - Firebase Firestore instance
 */
export const updateFamilyCompletion = async (familyId, book, chapter, verseNumbers, db) => {
  try {
    const { doc, getDoc, updateDoc, serverTimestamp, collection, getDocs, query, where } = await import('firebase/firestore');
    
    console.log(`📊 FAMILY COMPLETION: Updating completion for family ${familyId}, ${book} ${chapter}, verses: [${verseNumbers.join(', ')}]`);
    
    // Get the family completion document
    const completionRef = doc(db, `families/${familyId}/completedChapters`, 'all-chapters');
    const completionDoc = await getDoc(completionRef);
    
    if (!completionDoc.exists()) {
      console.log(`📊 FAMILY COMPLETION: No completion document found for family ${familyId}, creating one...`);
      await createFamilyCompletionSubcollection(familyId, db);
      // Recursive call to handle the update after creation
      return await updateFamilyCompletion(familyId, book, chapter, verseNumbers, db);
    }
    
    // Get all users in this family
    const usersSnapshot = await getDocs(
      query(
        collection(db, 'users'),
        where('familyIds', 'array-contains', familyId)
      )
    );
    
    const familyUserIds = usersSnapshot.docs.map(doc => doc.id);
    console.log(`📊 FAMILY COMPLETION: Found ${familyUserIds.length} family members`);
    
    if (familyUserIds.length === 0) {
      console.log(`📊 FAMILY COMPLETION: No family members found, skipping update`);
      return;
    }
    
    // Get ALL recordings for this chapter from ALL family members.
    // Query per-user with simple equality conditions to avoid needing a composite index.
    const allRecordings = [];
    for (const uid of familyUserIds) {
      const snap = await getDocs(
        query(
          collection(db, 'recordings'),
          where('userId', '==', uid),
          where('book', '==', book.toLowerCase()),
          where('chapter', '==', chapter)
        )
      );
      allRecordings.push(...snap.docs.map(d => d.data()));
    }
    console.log(`📊 FAMILY COMPLETION: Found ${allRecordings.length} recordings for ${book} ${chapter} from all family members`);
    
    // Aggregate all recorded verses from all family members
    const recordedVerses = new Set();
    for (const recording of allRecordings) {
      if (recording.verse) {
        recordedVerses.add(recording.verse);
      }
    }
    
    const recordedVerseNumbers = Array.from(recordedVerses).sort((a, b) => a - b);
    console.log(`📊 FAMILY COMPLETION: Aggregated verses: [${recordedVerseNumbers.join(', ')}]`);
    
    const completionData = completionDoc.data();
    const chapters = completionData.chapters || [];
    
    // Find the chapter in the completion data
    const chapterIndex = chapters.findIndex(ch => ch.book === book && ch.chapter === chapter);
    
    if (chapterIndex === -1) {
      console.error(`📊 FAMILY COMPLETION: Chapter ${book} ${chapter} not found in completion data`);
      console.error(`📊 FAMILY COMPLETION: Available chapters:`, chapters.map(ch => `${ch.book}_${ch.chapter}`).slice(0, 10));
      console.error(`📊 FAMILY COMPLETION: Looking for: ${book}_${chapter}`);
      return;
    }
    
    const chapterData = chapters[chapterIndex];
    
    // Get the actual verse count from Bible data
    const actualTotalVerses = getVersesForChapter(book, chapter).length;
    
    // Update the chapter data with aggregated data
    const updatedChapterData = {
      ...chapterData,
      totalVerses: actualTotalVerses,
      completedVerses: recordedVerseNumbers.length,
      completed: recordedVerseNumbers.length >= actualTotalVerses,
      recordedVerseNumbers: recordedVerseNumbers // Add the actual verse numbers
    };
    
    // Update the chapters array
    chapters[chapterIndex] = updatedChapterData;
    
    // Calculate total completed chapters
    const completedChapters = chapters.filter(ch => ch.completed).length;
    
    // Update the completion document
    await updateDoc(completionRef, {
      chapters: chapters,
      completedChapters: completedChapters,
      lastUpdated: serverTimestamp()
    });
    
    console.log(`✅ FAMILY COMPLETION: Updated ${book} ${chapter} - ${updatedChapterData.completedVerses}/${actualTotalVerses} verses, completed: ${updatedChapterData.completed}`);
    console.log(`📊 FAMILY COMPLETION: Recorded verse numbers: [${recordedVerseNumbers.join(', ')}]`);
    console.log(`📊 FAMILY COMPLETION: Chapter data after update:`, {
      book: updatedChapterData.book,
      chapter: updatedChapterData.chapter,
      totalVerses: updatedChapterData.totalVerses,
      completedVerses: updatedChapterData.completedVerses,
      completed: updatedChapterData.completed,
      recordedVerseNumbers: updatedChapterData.recordedVerseNumbers
    });
    
  } catch (error) {
    console.error('Error updating family completion:', error);
    throw error;
  }
};

/**
 * Create family completion subcollection with all Bible chapters
 * @param {string} familyId - The family ID
 * @param {Object} db - Firebase Firestore instance
 */
export const createFamilyCompletionSubcollection = async (familyId, db) => {
  try {
    const { collection, doc, setDoc, serverTimestamp } = await import('firebase/firestore');
    const { BIBLE_BOOKS, getChaptersForBook, getVersesForChapter } = await import('./bibleData');
    
    console.log(`📊 FAMILY COMPLETION: Creating completion subcollection for family ${familyId}`);
    
    // Get all Bible books and chapters
    const allChapters = [];
    
    for (const book of BIBLE_BOOKS) {
      const chapters = getChaptersForBook(book.id);
      for (const chapter of chapters) {
        const verses = getVersesForChapter(book.id, chapter);
        allChapters.push({
          book: book.id,
          chapter: chapter,
          totalVerses: verses.length,
          completedVerses: 0,
          completed: false,
          lastUpdated: new Date(),
          createdAt: new Date()
        });
      }
    }
    
    // Create the completion subcollection
    const completionRef = doc(db, `families/${familyId}/completedChapters`, 'all-chapters');
    await setDoc(completionRef, {
      chapters: allChapters,
      totalChapters: allChapters.length,
      completedChapters: 0,
      lastUpdated: serverTimestamp(),
      createdAt: serverTimestamp()
    });
    
    console.log(`✅ FAMILY COMPLETION: Created completion subcollection for family ${familyId} with ${allChapters.length} chapters`);
    
  } catch (error) {
    console.error(`Error creating completion subcollection for family ${familyId}:`, error);
    throw error;
  }
};

/**
 * Get family completion status for a specific chapter
 * @param {string} familyId - The family ID
 * @param {string} book - The book name
 * @param {number} chapter - The chapter number
 * @param {Object} db - Firebase Firestore instance
 * @returns {Object} Chapter completion data
 */
export const getFamilyChapterCompletion = async (familyId, book, chapter, db) => {
  try {
    const { doc, getDoc } = await import('firebase/firestore');
    
    const completionRef = doc(db, `families/${familyId}/completedChapters`, 'all-chapters');
    const completionDoc = await getDoc(completionRef);
    
    if (!completionDoc.exists()) {
      return null;
    }
    
    const completionData = completionDoc.data();
    const chapters = completionData.chapters || [];
    
    const chapterData = chapters.find(ch => ch.book === book && ch.chapter === chapter);
    return chapterData || null;
    
  } catch (error) {
    console.error('Error getting family chapter completion:', error);
    return null;
  }
};

/**
 * Get all family completion data
 * @param {string} familyId - The family ID
 * @param {Object} db - Firebase Firestore instance
 * @returns {Object} Complete family completion data
 */
export const getFamilyCompletionData = async (familyId, db) => {
  try {
    const { doc, getDoc } = await import('firebase/firestore');
    
    const completionRef = doc(db, `families/${familyId}/completedChapters`, 'all-chapters');
    const completionDoc = await getDoc(completionRef);
    
    if (!completionDoc.exists()) {
      return null;
    }
    
    return completionDoc.data();
    
  } catch (error) {
    console.error('Error getting family completion data:', error);
    return null;
  }
};

/**
 * Check if a family has completed a specific chapter
 * @param {string} familyId - The family ID
 * @param {string} book - The book name
 * @param {number} chapter - The chapter number
 * @param {Object} db - Firebase Firestore instance
 * @returns {boolean} True if chapter is completed
 */
export const isFamilyChapterCompleted = async (familyId, book, chapter, db) => {
  try {
    const chapterData = await getFamilyChapterCompletion(familyId, book, chapter, db);
    return chapterData ? chapterData.completed : false;
  } catch (error) {
    console.error('Error checking family chapter completion:', error);
    return false;
  }
};
